import { Component, OnInit, Input, OnDestroy, EventEmitter, Output } from '@angular/core';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { Subscription } from 'rxjs';
import { MessengerService } from 'src/app/core/service/messenger.service';
import { IntlService } from '@progress/kendo-angular-intl';
import { Message } from 'src/app/case/model/message';
import { MessageListService } from 'src/app/core/service/grid-data/grid-data-message.service';
import { State } from '@progress/kendo-data-query';
import { AppConfigService } from 'src/app/core/service/app-config.service';

@Component({
  selector: 'app-message-list-section',
  templateUrl: './message-list-section.component.html',
  styleUrls: ['./message-list-section.component.scss']
})
export class MessageListSectionComponent implements OnInit, OnDestroy
{
  @Input() public memberId: number;
  @Input() public caseId: number;

  @Output() public refresh: EventEmitter<any> = new EventEmitter();

  public pageSizes = [5, 10, 25, 50, 100];
  public messageDialogOpened = false;
  public messageEnableAdvanced = false;
  public messageDetailDialogOpened = false;
  public selectedMessage = null;

  /**
   * When composing a new message, indicate the default message destination username.
   */
  public messageSendTo = '';
  public savingMessage = false;
  public state: State = {
    skip: 0,
    filter: {
      logic: 'and',
      filters: []
    }
  };
  public messages = [];
  public gridView: GridDataResult;
  public gridData: any = {};
  public total: number;

  private messagingOpenComposeMessageSubscription: Subscription;

  constructor(
    private messageListService: MessageListService,
    private appConfigService: AppConfigService,
    private messengerService: MessengerService,
    public intl: IntlService)
  {
    // subscribe to open new message dialog within the component
    this.messagingOpenComposeMessageSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message.type === 'openComponentComposeMessageDialog')
        {
          const isDirectAddress = message.data !== undefined && message.data.isDirectAddress !== undefined ?
            message.data.isDirectAddress : false;
          this.composeMessage(message.data !== undefined && message.data.sendTo ? message.data.sendTo : null, isDirectAddress);
        }
        else if (message.type === 'reloadMessagesData')
        {
          this.getMessages();
        }
      }
    });
  }

  public get loading(): boolean
  {
    return this.messageListService.loading;
  }

  public ngOnInit(): void
  {
    this.state = {
      filter: {
        filters: [], logic: 'and'
      }, group: [], skip: 0,
      sort: [
        { field: 'sentOn', dir: 'desc' }
      ],
      take: this.appConfigService.getConfig().pageSize
    };

    if (this.memberId)
    {
      this.state.filter.filters.push({ field: 'memberId', operator: 'eq', value: this.memberId });
    }

    if (this.caseId)
    {
      this.state.filter.filters.push({ field: 'caseId', operator: 'eq', value: this.caseId });
    }

    this.getMessages();
  }

  public ngOnDestroy(): void
  {
    if (this.messagingOpenComposeMessageSubscription !== undefined) { this.messagingOpenComposeMessageSubscription.unsubscribe(); }
  }

  /**
   * Create a new message.
   */
  public composeMessage(sendTo: string = null, isDirectAddress = false): void
  {
    this.messageSendTo = isDirectAddress ? `direct-${sendTo}` : sendTo;
    this.messageDialogOpened = true;
  }

  /**
   * Opens the selected message in a modal.
   *
   * @param message The message to open.
   */
  public openMessageDetails(message: Message): void
  {
    this.selectedMessage = message;
    this.messageDetailDialogOpened = true;
  }

  public closeDialogAndRefresh()
  {
    this.messageDialogOpened = false;
    this.state.skip = 0;
    this.getMessages();
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.getMessages();
  }

  private getMessages(): void
  {
    this.messageListService.queryByState(this.state, (messages: any) =>
    {
      if (messages !== null)
      {
        messages.data.forEach(d =>
        {
          d.sentOn = new Date(d.sentOn);
          d.toAddresses = d.to.map(x => x.name).join();
          d.from = d.fromDirectAddress ? d.fromDirectAddress : d.from;
          if (d.body.indexOf('\r\r\r') > -1)
          {
            d.body = d.body.substring(0, d.body.indexOf('\r\r\r'));
          }
        });
        this.messages = messages.data;
        this.gridView = {
          data: (messages) ? this.gridData = messages.data : this.gridData = [],
          total: (messages) ? this.total = messages.total : this.total = 0
        };
        this.refresh.emit();
      }
    });
  }
}
